/**
 * Base Player Class
 *
 * Shared player state and game logic used by both server and client
 * Server uses this for authoritative game state
 * Client extends this with rendering/interpolation (RemotePlayer, LocalPlayer)
 */

import { getDefaultWeapon } from './WeaponRegistry.js';
import { ItemType } from './Item.js';
import { itemRegistry } from './ItemRegistry.js';
import { refKey, refForItem } from './ItemRef.js';
import { ammoRegistry } from './AmmoRegistry.js';

/** @typedef {import('./Item.js').Item} Item */
/** @typedef {import('./Weapon.js').Weapon} Weapon */

/**
 * Rebuild one inventory slot from its wire form.
 *
 * The wire carries a ref and a stack; the live slot carries the item instance
 * and, for weapons, the mutable `weaponState` (ammo, reload timers) that the
 * wire deliberately does not round-trip. Resolution goes through the
 * itemRegistry façade, so a slot holding any registered kind rebuilds — before
 * 5D this asked `weaponRegistry` directly and silently dropped anything else.
 *
 * A slot that cannot be resolved keeps its previous contents rather than
 * emptying: a registry that has not finished loading must not cost the player
 * their inventory.
 *
 * @param {{ ref?: string, itemId?: string, stack?: number }|null} slot wire slot
 * @param {object|null} previousSlot the live slot this one replaces
 * @param {(slot: object) => void} [onUnresolved] called when the ref names
 *        nothing this environment has registered — the client logs it, because
 *        it means its registries disagree with the server's
 * @returns {{ item: Item, stack: number, weaponState?: object }|null}
 */
function rebuildInventorySlot(slot, previousSlot = null, onUnresolved = null) {
  if (!slot) return null;

  // `itemId` is the pre-5D spelling and always meant a weapon.
  const item = itemRegistry.get(slot.ref ?? { weaponId: slot.itemId });
  if (!item) {
    onUnresolved?.(slot);
    return previousSlot || null;
  }

  const slotData = { item, stack: slot.stack };

  if (item.type === ItemType.WEAPON) {
    const reusable = previousSlot?.item?.id === item.id && previousSlot.weaponState;
    slotData.weaponState = reusable ? previousSlot.weaponState : item.createState();
  }

  return slotData;
}

/**
 * Write server-sent ammo for the non-active weapon slots into the local
 * inventory's weaponState objects. The active slot is skipped: its ammo
 * arrives in the `weapon` field (and, on the local player, is what prediction
 * has been mutating).
 *
 * @param {Array<{weaponState?: object}|null>} inventory
 * @param {Array<{currentAmmo: number}|null>|undefined} slotAmmo
 * @param {number} activeSlotIndex
 */
function applySlotAmmo(inventory, slotAmmo, activeSlotIndex) {
  if (!Array.isArray(slotAmmo)) return;
  slotAmmo.forEach((ammo, i) => {
    if (!ammo || i === activeSlotIndex) return;
    const state = inventory[i]?.weaponState;
    if (!state) return;
    state.currentAmmo = ammo.currentAmmo;
  });
}

  class Player {
    constructor(config) {
      // Core identity
      this.id = config.id;
      this.name = config.name || 'Player';

      // Position and movement (managed by physics on server, interpolated on client)
      this.x = config.x || 0;
      this.y = config.y || 0;
      this.vx = config.vx || 0;
      this.vy = config.vy || 0;
      this.angle = config.angle || 0;
      // Drift (hold-to-slide) state — owned by the movement simulation (shared/Movement.js).
      // null until the server initialises it on spawn; clients receive it in snapshots.
      this.drift = config.drift || null;

      // Health system
      this.health = {
        maxHealth: config.maxHealth || 100,
        currentHealth: config.currentHealth || 100
      };
      this.shieldHealth = Number.isFinite(config.shieldHealth) ? config.shieldHealth : 0;
      this.maxShield = Number.isFinite(config.maxShield) ? config.maxShield : null;

      // The ammo pouch: rounds per ammo type, shared by every gun of that type.
      // Never an inventory slot. Mutated in place; serialize() sends a copy.
      this.ammo = { ...(config.ammo || {}) };

      // Passive health regen bookkeeping (server-only; never serialized).
      // lastDamageTime: last resolved incoming hit, landed or blocked — stamped
      // by CombatSystem. lastRegenTick: PlayerSystem's own anchor for how much
      // health to add on each regen call; null while not currently accruing.
      this.lastDamageTime = Date.now();
      this.lastRegenTick = null;

      // Cosmetics (skins, trails, effects)
      this.cosmetics = config.cosmetics || {
        equippedSkin: 'default',
        equippedTrail: null,
        equippedBackpack: null,
        equippedEmote: null
      };

      // Inventory system (5 slots for equippable items)
      // Each slot: { item: Item, stack: number } or null
      this.inventory = (config.inventory || this._initializeInventory(config)).map(slot => {
        if (!slot) return null;
        if (slot.item && slot.item.type === ItemType.WEAPON) {
          slot.weaponState = slot.weaponState || slot.item.createState();
        }
        return slot;
      });
      this.activeSlotIndex = config.activeSlotIndex !== undefined ? config.activeSlotIndex : 0;

      // Backward compatibility: equippedWeapon is now a getter
      // that returns the active slot's item if it's a weapon
      this._weaponState = null;
      this._emptyWeaponState = {
        currentAmmo: 0,
        reloading: false,
        reloadStartTime: null,
        lastShotTime: 0,
        unlimitedAmmo: false
      };
      this._syncActiveWeaponState();

      this.stats = config.stats || {
        kills: 0,
        deaths: 0,
        damageDealt: 0,
        damageTaken: 0,
        score: 0
      };
    }

    /**
     * Initialize inventory with default weapon
     * @param {Object} config - Constructor config
     * @returns {Array} - Inventory array
     * @private
     */
    _initializeInventory(config) {
      const inventory = [null, null, null, null, null];

      // If equippedWeapon provided (for backward compatibility), put it in slot 0
      if (config.equippedWeapon) {
        inventory[0] = {
          item: config.equippedWeapon,
          stack: 1,
          weaponState: config.equippedWeapon.createState()
        };
      } else {
        // Default: give player a starting weapon in slot 0
        const defaultWeapon = getDefaultWeapon();
        if (defaultWeapon) {
          inventory[0] = {
            item: defaultWeapon,
            stack: 1,
            weaponState: defaultWeapon.createState()
          };
        }
      }

      return inventory;
    }

    _ensureSlotWeaponState(slot) {
      if (!slot) return null;
      const item = slot.item;
      if (!item || item.type !== ItemType.WEAPON) {
        return null;
      }
      if (!slot.weaponState) {
        slot.weaponState = item.createState();
      }
      return slot.weaponState;
    }

    _syncActiveWeaponState() {
      const slot = this.inventory[this.activeSlotIndex];
      this._weaponState = this._ensureSlotWeaponState(slot);
    }

    /**
     * Get the currently equipped/active item
     * @returns {Item|null}
     */
    get equippedItem() {
      const slot = this.inventory[this.activeSlotIndex];
      return slot ? slot.item : null;
    }

    /**
     * Get the currently equipped weapon (backward compatibility)
     * @returns {Weapon|null}
     */
    get equippedWeapon() {
      const item = this.equippedItem;
      return (item && item.type === ItemType.WEAPON) ? /** @type {Weapon} */ (item) : null;
    }

    /**
     * Get weapon state for the active weapon
     * @returns {Object}
     */
    get weaponState() {
      const weapon = this.equippedWeapon;
      if (!weapon) {
        return this._emptyWeaponState;
      }

      const slot = this.inventory[this.activeSlotIndex];
      this._weaponState = this._ensureSlotWeaponState(slot);
      return this._weaponState || this._emptyWeaponState;
    }

    set weaponState(state) {
      this._weaponState = state;
      const slot = this.inventory[this.activeSlotIndex];
      if (slot && slot.item && slot.item.type === ItemType.WEAPON) {
        slot.weaponState = state;
      }
    }

    /**
     * Take damage from an attack
     * @param {number} amount - Damage amount
     * @param {string} attackerId - ID of attacking player (optional)
     * @returns {{died: boolean, shieldDamage: number, healthDamage: number}}
     */
    takeDamage(amount, attackerId = null) {
      if (amount <= 0) {
        return { died: false, shieldDamage: 0, healthDamage: 0 };
      }

      this.stats.damageTaken += amount;

      let remainingDamage = amount;
      let shieldDamage = 0;
      let healthDamage = 0;

      if (this.shieldHealth > 0) {
        shieldDamage = Math.min(this.shieldHealth, remainingDamage);
        this.shieldHealth -= shieldDamage;
        remainingDamage -= shieldDamage;
      }

      if (remainingDamage > 0) {
        healthDamage = remainingDamage;
        this.health.currentHealth -= remainingDamage;
      }

      const died = this.health.currentHealth <= 0;
      if (died) {
        this.health.currentHealth = 0;
        this.stats.deaths++;
      }

      return { died, shieldDamage, healthDamage };
    }

    /**
     * Take damage directly to health, bypassing shields (used for zone damage)
     * @param {number} amount - Damage to apply directly to health
     * @returns {{died: boolean, healthDamage: number}}
     */
    takeHealthDamage(amount) {
      if (amount <= 0) {
        return { died: false, healthDamage: 0 };
      }

      this.stats.damageTaken += amount;
      this.health.currentHealth -= amount;

      const died = this.health.currentHealth <= 0;
      if (died) {
        this.health.currentHealth = 0;
        this.stats.deaths++;
      }

      return { died, healthDamage: amount };
    }

    /**
     * Heal the player
     * @param {number} amount - Heal amount
     */
    heal(amount) {
      this.health.currentHealth = Math.min(
        this.health.currentHealth + amount,
        this.health.maxHealth
      );
    }

    /**
     * Check if player is alive
     */
    isAlive() {
      return this.health.currentHealth > 0;
    }

    /**
     * Equip a new skin
     * @param {string} skinId - Skin identifier
     */
    equipSkin(skinId) {
      if (typeof skinId !== 'string' || skinId.length === 0) {
        return false;
      }
      this.cosmetics.equippedSkin = skinId;
      return true;
    }

    /**
     * Equip a movement trail (null/'' clears back to the default trail)
     * @param {string|null} trailId
     */
    equipTrail(trailId) {
      if (trailId !== null && typeof trailId !== 'string') {
        return false;
      }
      this.cosmetics.equippedTrail = trailId || null;
      return true;
    }

    /**
     * Equip a backpack (null/'' clears back to the default backpack; the
     * catalog's 'none' entry is how a player wears no backpack)
     * @param {string|null} backpackId
     */
    equipBackpack(backpackId) {
      if (backpackId !== null && typeof backpackId !== 'string') {
        return false;
      }
      this.cosmetics.equippedBackpack = backpackId || null;
      return true;
    }

    /**
     * Switch to a specific inventory slot
     * @param {number} slotIndex - Slot index (0-4)
     * @returns {boolean} - Success
     */
    switchToSlot(slotIndex) {
      if (slotIndex < 0 || slotIndex >= 5) {
        return false;
      }

      // Interrupt reload on the current weapon before switching
      if (this.equippedWeapon && this.weaponState && this.weaponState.reloading) {
        this.equippedWeapon.interruptReload(this.weaponState);
      }

      this.activeSlotIndex = slotIndex;
      this._syncActiveWeaponState();
      return true;
    }

    /**
     * Switch to next/previous inventory slot (wraps around, skips empty slots)
     * @param {number} direction - 1 for next, -1 for previous
     */
    switchSlot(direction = 1) {
      const startIndex = this.activeSlotIndex;
      let newIndex = startIndex;
      let attempts = 0;

      do {
        newIndex = newIndex + direction;

        // Wrap around
        if (newIndex < 0) {
          newIndex = 4;
        } else if (newIndex > 4) {
          newIndex = 0;
        }

        attempts++;

        // If we've checked all slots, stay on current
        if (attempts >= 5) {
          return;
        }

        // Check if this slot has an item
        if (this.inventory[newIndex] !== null) {
          this.switchToSlot(newIndex);
          return;
        }
      } while (newIndex !== startIndex);
    }

    /**
     * Add an item to inventory
     * @param {Item} item - Item to add
     * @param {number} stack - Stack count
     * @returns {Object} - {success: boolean, slotIndex: number, reason: string}
     */
    addItemToInventory(item, stack = 1) {
      if (!item || !item.isEquippable) {
        return { success: false, reason: 'Item not equippable' };
      }

      // Try to stack with existing item
      if (item.isStackable) {
        for (let i = 1; i < 5; i++) {
          const slot = this.inventory[i];
          if (slot && slot.item.id === item.id) {
            const newStack = slot.stack + stack;
            if (newStack <= item.maxStackSize) {
              slot.stack = newStack;
              return { success: true, slotIndex: i, stacked: true };
            }
          }
        }
      }

      // Find empty slot
      for (let i = 1; i < 5; i++) {
        if (!this.inventory[i]) {
          const slotData = { item, stack };
          if (item.type === ItemType.WEAPON) {
            slotData.weaponState = /** @type {Weapon} */ (item).createState();
          }
          this.inventory[i] = slotData;
          return { success: true, slotIndex: i };
        }
      }

      return { success: false, reason: 'Inventory full' };
    }

    /**
     * Remove item from active slot (called when picking up new item)
     * @returns {Object|null} - Removed {item, stack} or null
     */
    removeFromActiveSlot() {
      const slot = this.inventory[this.activeSlotIndex];
      if (!slot) return null;

      this.inventory[this.activeSlotIndex] = null;
      this._weaponState = null; // Clear weapon state

      return slot;
    }

    // ─────────────────────────────────────────────────────────────
    // WEAPON INVENTORY MANAGEMENT (for weapon pickup system)
    // ─────────────────────────────────────────────────────────────

    /**
     * Find a weapon in inventory by weapon ID
     * @param {string} weaponId - Weapon ID to find
     * @returns {number} - Slot index if found, -1 if not
     */
    findWeaponInInventory(weaponId) {
      for (let i = 0; i < 5; i++) {
        const slot = this.inventory[i];
        if (slot && slot.item && slot.item.id === weaponId) {
          return i;
        }
      }
      return -1;
    }

    /**
     * Get the first empty inventory slot
     * @returns {number} - Empty slot index, or -1 if full
     */
    getFirstEmptySlot() {
      for (let i = 1; i < 5; i++) {
        if (!this.inventory[i]) {
          return i;
        }
      }
      return -1;
    }

    /**
     * Check if inventory is full (no empty slots)
     * @returns {boolean}
     */
    isInventoryFull() {
      return this.getFirstEmptySlot() === -1;
    }

    /** @returns {number} rounds of one type in the pouch */
    getAmmo(type) {
      return Math.max(0, this.ammo?.[type] || 0);
    }

    /** @returns {number} rounds of this type that still fit (Infinity with no cap) */
    ammoRoom(type) {
      const cap = ammoRegistry.get(type)?.pouchCap;
      return Number.isFinite(cap) ? Math.max(0, cap - this.getAmmo(type)) : Infinity;
    }

    /**
     * Put rounds in the pouch, clamped to the type's cap.
     * @returns {number} rounds actually added
     */
    addAmmo(type, amount) {
      if (!type || !(amount > 0)) return 0;
      const added = Math.min(Math.floor(amount), this.ammoRoom(type));
      if (added > 0) this.ammo[type] = this.getAmmo(type) + added;
      return added;
    }

    /**
     * Get the stack count for a weapon in a specific slot
     * @param {number} slotIndex - Slot index
     * @returns {number} - Stack count, or 0 if slot is empty
     */
    getWeaponStacks(slotIndex) {
      const slot = this.inventory[slotIndex];
      return slot ? (slot.stack || 1) : 0;
    }

    /**
     * Check if a weapon can be stacked (exists and not at max stacks)
     * @param {string} weaponId - Weapon ID to check
     * @returns {{canStack: boolean, slotIndex: number, currentStacks: number, maxStacks: number}}
     */
    canStackWeapon(weaponId) {
      const slotIndex = this.findWeaponInInventory(weaponId);
      if (slotIndex === -1) {
        return { canStack: false, slotIndex: -1, currentStacks: 0, maxStacks: 0 };
      }

      const slot = this.inventory[slotIndex];
      const currentStacks = slot.stack || 1;
      const maxStacks = slot.item.maxStacks || slot.item.maxStackSize || 4;
      const canStack = currentStacks < maxStacks;

      return { canStack, slotIndex, currentStacks, maxStacks };
    }

    /**
     * Add stacks to an existing weapon in a slot
     * @param {number} slotIndex - Slot index
     * @param {number} additionalStacks - Stacks to add
     * @returns {{success: boolean, newStacks: number}}
     */
    stackWeaponInSlot(slotIndex, additionalStacks = 1) {
      const slot = this.inventory[slotIndex];
      if (!slot || !slot.item) {
        return { success: false, newStacks: 0 };
      }

      const maxStacks = slot.item.maxStacks || slot.item.maxStackSize || 4;
      const currentStacks = slot.stack || 1;
      const newStacks = Math.min(currentStacks + additionalStacks, maxStacks);

      slot.stack = newStacks;
      return { success: true, newStacks };
    }

    /**
     * Remove item from a specific slot
     * @param {number} slotIndex - Slot to remove from
     * @returns {Object|null} - Removed {item, stack, weaponState} or null
     */
    dropFromSlot(slotIndex) {
      if (slotIndex <= 0 || slotIndex >= 5) {
        return null; // slot 0 holds the pickaxe: never droppable
      }

      const slot = this.inventory[slotIndex];
      if (!slot) return null;

      // Store the slot data before clearing
      const droppedData = {
        item: slot.item,
        stack: slot.stack || 1,
        weaponState: slot.weaponState || null
      };

      this.inventory[slotIndex] = null;

      // If we dropped from active slot, sync weapon state
      if (slotIndex === this.activeSlotIndex) {
        this._weaponState = null;
      }

      return droppedData;
    }

    /**
     * Add an item to a specific slot
     * @param {number} slotIndex - Target slot
     * @param {Item} item - Item to add
     * @param {number} stack - Stack count
     * @returns {boolean} - Success
     */
    addItemToSlot(slotIndex, item, stack = 1) {
      if (slotIndex <= 0 || slotIndex >= 5) {
        return false; // slot 0 holds the pickaxe: never a pickup target
      }
      if (!item) {
        return false;
      }

      const slotData = { item, stack };
      if (item.type === ItemType.WEAPON) {
        slotData.weaponState = /** @type {Weapon} */ (item).createState();
      }

      this.inventory[slotIndex] = slotData;

      // If adding to active slot, sync weapon state
      if (slotIndex === this.activeSlotIndex) {
        this._syncActiveWeaponState();
      }

      return true;
    }

    /**
     * Swap two inventory slots
     * @param {number} fromIndex - Source slot index
     * @param {number} toIndex - Target slot index
     * @returns {boolean} - Success
     */
    swapInventorySlots(fromIndex, toIndex) {
      if (fromIndex <= 0 || fromIndex >= 5 || toIndex <= 0 || toIndex >= 5) {
        return false; // slot 0 holds the pickaxe: never swappable
      }
      if (fromIndex === toIndex) {
        return false; // No-op
      }

      // Swap the slots
      const temp = this.inventory[fromIndex];
      this.inventory[fromIndex] = this.inventory[toIndex];
      this.inventory[toIndex] = temp;

      // If either slot was the active slot, resync weapon state
      if (fromIndex === this.activeSlotIndex || toIndex === this.activeSlotIndex) {
        this._syncActiveWeaponState();
      }

      return true;
    }

    /**
     * Use the currently active item
     * @param {Object} context - Usage context
     * @returns {Object} - Usage result
     */
    useActiveItem(context) {
      const slot = this.inventory[this.activeSlotIndex];
      if (!slot) {
        return { success: false, reason: 'No item in active slot' };
      }

      const { item } = slot;

      // Use the item (polymorphic behavior)
      const result = item.use({ ...context, player: this });

      // If consumed (e.g., potion), decrement stack
      if (result.consumed) {
        slot.stack--;
        if (slot.stack <= 0) {
          this.inventory[this.activeSlotIndex] = null;
        }
      }

      return result;
    }

    /**
     * Equip a weapon (backward compatibility method)
     * @param {Weapon} weapon - Weapon instance
     */
    equipWeapon(weapon) {
      // Slot 0 is reserved for the pickaxe — a legacy equip (spawn, or Gun
      // Game's per-kill progression) targets slot 1 instead when the active
      // slot happens to be 0, same fallback WeaponSystem's pickup-replace uses.
      const targetSlot = this.activeSlotIndex === 0 ? 1 : this.activeSlotIndex;
      if (!weapon) {
        this.inventory[targetSlot] = null;
        this.activeSlotIndex = targetSlot;
        this._weaponState = null;
        return;
      }
      this.inventory[targetSlot] = {
        item: weapon,
        stack: 1,
        weaponState: weapon.createState()
      };
      this.activeSlotIndex = targetSlot;
      this._syncActiveWeaponState();
    }

    /**
     * Respawn the player
     * @param {number} x - Spawn X position
     * @param {number} y - Spawn Y position
     */
    respawn(x, y) {
      this.x = x;
      this.y = y;
      this.vx = 0;
      this.vy = 0;
      this.angle = 0;
      this.health.currentHealth = this.health.maxHealth;
      this.lastDamageTime = Date.now();
      this.lastRegenTick = null;

      // Reset weapon state for active weapon
      const weapon = this.equippedWeapon;
      if (weapon) {
        const slot = this.inventory[this.activeSlotIndex];
        if (slot) {
          slot.weaponState = weapon.createState();
        }
        this._syncActiveWeaponState();
      } else {
        this._weaponState = null;
      }
    }

    /**
     * Serialize player for network transmission
     * @returns {Object} - Serialized player state
     */
    serialize() {
        /** @type {any} */
        const state = {
        id: this.id,
        name: this.name,
        x: this.x,
        y: this.y,
        vx: this.vx,
        vy: this.vy,
        angle: this.angle,
        health: {
          maxHealth: this.health.maxHealth,
          currentHealth: this.health.currentHealth
        },
        cosmetics: this.cosmetics,
        team: this.team,
        shieldHealth: this.shieldHealth,
        ...(Number.isFinite(this.maxShield) ? { maxShield: this.maxShield } : {}),
        drift: this.drift ? { meter: this.drift.meter, active: this.drift.active, dx: this.drift.dx, dy: this.drift.dy, held: this.drift.held, regenDelay: this.drift.regenDelay } : null,
        activeSlotIndex: this.activeSlotIndex,
        ammo: { ...(this.ammo || {}) },
        inventory: this.inventory.map(slot => {
          if (!slot) return null;
          return {
            // `ref` is the 5D field; `itemId` is kept for one release so a
            // client from before the change still finds its weapons.
            ref: refKey(refForItem(slot.item)),
            itemId: slot.item.id,
            stack: slot.stack
          };
        }),
        // Ammo for the non-active weapon slots (the active weapon's rides in
        // `weapon`). Null for the active slot, so this only changes on
        // switch/respawn/pickup — never per shot.
        slotAmmo: this.inventory.map((slot, i) => {
          if (!slot || i === this.activeSlotIndex || !slot.weaponState) return null;
          return { currentAmmo: slot.weaponState.currentAmmo };
        })
      };

      // Backward compatibility: include weapon field for active weapon
      const weapon = this.equippedWeapon;
      if (weapon) {
        state.weapon = {
          id: weapon.id,
          currentAmmo: this.weaponState.currentAmmo,
          reloading: this.weaponState.reloading,
          reloadStartTime: this.weaponState.reloadStartTime,
          reloadTime: this.weaponState.reloadTime || weapon.reloadTime  // Use actual reload time if set
        };
      }

      // Include active ability effects if any (for rendering on other clients)
      if (this.activeAbilityEffects && Object.keys(this.activeAbilityEffects).length > 0) {
        state.activeAbilityEffects = this.activeAbilityEffects;
      }

      state.stats = {
        kills: this.stats?.kills || 0,
        deaths: this.stats?.deaths || 0,
        damageDealt: this.stats?.damageDealt || 0,
        damageTaken: this.stats?.damageTaken || 0,
        score: this.stats?.score || 0
      };

      return state;
    }

    /**
     * Update from serialized state (used by client)
     * @param {Object} state - Serialized player state
     */
    updateFromState(state) {
      this.x = state.x;
      this.y = state.y;
      this.vx = state.vx;
      this.vy = state.vy;
      this.angle = state.angle;

      if (state.health) {
        this.health = state.health;
      }

      if (typeof state.shieldHealth === 'number') {
        this.shieldHealth = state.shieldHealth;
      }

      if (typeof state.maxShield === 'number') {
        this.maxShield = state.maxShield;
      }

      if (state.ammo && typeof state.ammo === 'object') this.ammo = { ...state.ammo };

      if ('drift' in state) {
        this.drift = state.drift;
      }

      if (state.cosmetics) {
        this.cosmetics = state.cosmetics;
      }

      if (state.team !== undefined) {
        this.team = state.team;
      }

      // Update inventory if present
      if (state.inventory) {
        const previousInventory = this.inventory || [];
        this.inventory = state.inventory.map((slot, index) =>
          rebuildInventorySlot(slot, previousInventory[index]));
      }

      // Update active slot index
      if (typeof state.activeSlotIndex === 'number') {
        this.activeSlotIndex = state.activeSlotIndex;
      }

      if ('slotAmmo' in state) {
        applySlotAmmo(this.inventory, state.slotAmmo, this.activeSlotIndex);
      }

      // Update weapon state (backward compatibility and for active weapon)
      if (state.weapon) {
        const currentWeaponState = this.weaponState;
        currentWeaponState.currentAmmo = state.weapon.currentAmmo;
        currentWeaponState.reloading = state.weapon.reloading;
        currentWeaponState.reloadStartTime = state.weapon.reloadStartTime;
        currentWeaponState.reloadTime = state.weapon.reloadTime;
      }

      if (state.stats) {
        this.stats = {
          ...this.stats,
          ...state.stats
        };
      }

      // Update active ability effects (for rendering invisibility/invulnerability)
      // Only update if explicitly included in state (delta compression may omit unchanged fields)
      if ('activeAbilityEffects' in state) {
        this.activeAbilityEffects = state.activeAbilityEffects || {};
      }

      this._syncActiveWeaponState();
    }
  }

export { Player, rebuildInventorySlot, applySlotAmmo };
